import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi } from '../api/productsApi'
import { useToast } from '../../../shared/components/toast/useToast'
import type { ProductsParams, ProductStatus } from '../types'
import { useAuthStore } from '../../auth/store'

export const useProducts = (params?: ProductsParams) => {
  const selectedTenantId = useAuthStore((state) => state.selectedTenantId)
  return useQuery({
    queryKey: ['products', selectedTenantId, params],
    queryFn: () => productsApi.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useDraftProducts = (params?: ProductsParams) => {
  const selectedTenantId = useAuthStore((state) => state.selectedTenantId)
  return useQuery({
    queryKey: ['products', 'drafts', selectedTenantId, params],
    queryFn: () => productsApi.drafts.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useProduct = (id: string, status: ProductStatus) => {
  return useQuery({
    queryKey: ['product', id, status],
    queryFn: () => productsApi.getByIdAndStatus(id, status),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useDeleteProduct = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  
  return useMutation({
    mutationFn: ({ id, isDraft }: { id: string; isDraft: boolean }) => {
      return isDraft ? productsApi.drafts.delete(id) : productsApi.delete(id)
    },
    onSuccess: (_, { id, isDraft }) => {
      // Remove the specific product from cache
      queryClient.removeQueries({ queryKey: ['product', id] })
      
      // Invalidate all product list queries
      if (isDraft) {
        queryClient.invalidateQueries({ queryKey: ['products', 'drafts'], exact: false })
      } else {
        queryClient.invalidateQueries({ queryKey: ['products'], exact: false })
      }
      
      toast.success('Product deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete product', 'Please try again later')
    }
  })
}

export const usePublishProduct = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  
  return useMutation({
    mutationFn: (id: string) => {
      return productsApi.drafts.publish(id)
    },
    onSuccess: () => {
      // Invalidate both draft and published queries since product moves between them
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      
      toast.success('Product published successfully')
    },
    onError: () => {
      toast.error('Failed to publish product', 'Please try again later')
    }
  })
}